/* EditorialSkeleton — minimal full-bleed loader for tenant editorial pages.

   Why this exists: every editorial template page (Home / Portfolio / Services
   / Contact) used to render with fallback content ("Studio", "Light, shape,
   presence.", a stock Unsplash hero) during the ~300-800ms window between
   first paint and the /api/site response. Returning visitors hit
   localStorage and skip this entirely; first-time visitors see this for the
   loading window instead of misleading fake content.

   Design: stays on-brand without claiming to BE the tenant. Dark charcoal
   ground, a single gold horizon rule with a slow shimmer, and a tiny gold
   "loading" eyebrow. The shimmer animation is CSS-only so no extra JS bundle
   weight. The skeleton intentionally does NOT mimic the eventual layout
   (no fake hero blocks, no fake nav) so the first content flash IS the real
   content — no double layout shift.

   Variant param lets pages tune the eyebrow text. Defaults to a generic
   "Loading the studio." which works for every route. */

const TONE = {
  bg: "#0E0D0C",
  fg: "#F5F2EC",
  gold: "#C9A961",
};

export function EditorialSkeleton({ eyebrow = "Loading the studio." }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: TONE.bg, color: TONE.fg }}
      data-testid="editorial-skeleton"
      aria-busy="true"
      role="status"
    >
      {/* Subtle film-grain feel via radial gradient (no image asset to fetch) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(245,242,236,0.03) 0%, rgba(14,13,12,0) 60%)",
        }}
      />

      <div className="relative text-center max-w-sm px-8">
        {/* Tiny eyebrow */}
        <div
          className="text-[10px] uppercase tracking-[0.42em]"
          style={{ color: TONE.gold, opacity: 0.85 }}
        >
          {eyebrow}
        </div>

        {/* Shimmer horizon — the only animated element. Hand-tuned so the
            gold sweep is barely-there: the eye notices motion before the
            brain notices the bar exists. */}
        <div
          className="mx-auto mt-7 overflow-hidden"
          style={{
            width: "8rem",
            height: "1px",
            background: "rgba(201,169,97,0.18)",
          }}
        >
          <div
            className="h-full editorial-skeleton-shimmer"
            style={{
              width: "40%",
              background: `linear-gradient(90deg, transparent 0%, ${TONE.gold} 50%, transparent 100%)`,
            }}
          />
        </div>

        {/* Faint ambient watermark — disambiguates "this is loading" from
            "the site is broken". Editorial style only — no spinner, no
            Tailwind-template loading bar. */}
        <div
          className="mt-7 font-serif italic text-sm"
          style={{ color: "rgba(245,242,236,0.45)" }}
        >
          one moment
        </div>
      </div>

      <style>{`
        @keyframes editorial-skeleton-shimmer-keyframes {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
        .editorial-skeleton-shimmer {
          animation: editorial-skeleton-shimmer-keyframes 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
}

export default EditorialSkeleton;
